const RANK_MAP: Record<string, string> = { T: '10', J: 'J', Q: 'Q', K: 'K', A: 'A' };
const SUIT_MAP: Record<string, 'hearts' | 'diamonds' | 'clubs' | 'spades'> = {
  h: 'hearts',
  d: 'diamonds',
  c: 'clubs',
  s: 'spades',
};

/** Convert server card string "Th" | "As" to { suit, rank } for Card component */
export function parseCardStr(str: string): { suit: 'hearts' | 'diamonds' | 'clubs' | 'spades'; rank: string } {
  const rankChar = str.slice(0, -1);
  const suitChar = str.slice(-1);
  return {
    suit: SUIT_MAP[suitChar] ?? 'spades',
    rank: RANK_MAP[rankChar] ?? rankChar,
  };
}

/* ═══════════════════════════════════════════════════════════
   Client-side hand evaluator
   Given 2 hole cards + 0-5 community cards, returns the best
   5-card poker hand name.
   ═══════════════════════════════════════════════════════════ */

const RANK_ORDER = '23456789TJQKA';

function cardValue(card: string): { r: number; s: string } {
  const s = card.slice(-1);
  const rStr = card.slice(0, -1);
  const r = RANK_ORDER.indexOf(rStr.length === 2 ? 'T' : rStr);
  return { r: r >= 0 ? r : 0, s };
}

function combinations<T>(arr: T[], k: number): T[][] {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const result: T[][] = [];
  for (let i = 0; i <= arr.length - k; i++) {
    for (const rest of combinations(arr.slice(i + 1), k - 1)) {
      result.push([arr[i], ...rest]);
    }
  }
  return result;
}

type HandRank = {
  tier: number; // 9=straight flush, 8=quads, 7=full house, ... 0=high card
  kickers: number[];
  name: string;
};

function evaluate5(cards: string[]): HandRank {
  const parsed = cards.map(cardValue).sort((a, b) => b.r - a.r);
  const ranks = parsed.map((c) => c.r);
  const suits = parsed.map((c) => c.s);

  const isFlush = suits.every((s) => s === suits[0]);

  let isStraight = false;
  let straightHigh = 0;
  if (ranks[0] - ranks[4] === 4 && new Set(ranks).size === 5) {
    isStraight = true;
    straightHigh = ranks[0];
  }
  // Ace-low straight (A-2-3-4-5)
  if (
    ranks[0] === 12 &&
    ranks[1] === 3 &&
    ranks[2] === 2 &&
    ranks[3] === 1 &&
    ranks[4] === 0
  ) {
    isStraight = true;
    straightHigh = 3; // 5-high
  }

  const counts: Record<number, number> = {};
  for (const r of ranks) counts[r] = (counts[r] || 0) + 1;
  const groups = Object.entries(counts)
    .map(([r, c]) => ({ rank: Number(r), count: c }))
    .sort((a, b) => b.count - a.count || b.rank - a.rank);

  if (isStraight && isFlush) {
    if (straightHigh === 12) return { tier: 9, kickers: [straightHigh], name: 'Royal Flush' };
    return { tier: 8, kickers: [straightHigh], name: 'Straight Flush' };
  }
  if (groups[0].count === 4) {
    return { tier: 7, kickers: [groups[0].rank, groups[1].rank], name: 'Four of a Kind' };
  }
  if (groups[0].count === 3 && groups[1].count === 2) {
    return { tier: 6, kickers: [groups[0].rank, groups[1].rank], name: 'Full House' };
  }
  if (isFlush) {
    return { tier: 5, kickers: ranks, name: 'Flush' };
  }
  if (isStraight) {
    return { tier: 4, kickers: [straightHigh], name: 'Straight' };
  }
  if (groups[0].count === 3) {
    const kickers = groups.slice(1).map((g) => g.rank);
    return { tier: 3, kickers: [groups[0].rank, ...kickers], name: 'Three of a Kind' };
  }
  if (groups[0].count === 2 && groups[1].count === 2) {
    const pairs = [groups[0].rank, groups[1].rank].sort((a, b) => b - a);
    return { tier: 2, kickers: [...pairs, groups[2].rank], name: 'Two Pair' };
  }
  if (groups[0].count === 2) {
    const kickers = groups.slice(1).map((g) => g.rank);
    return { tier: 1, kickers: [groups[0].rank, ...kickers], name: 'Pair' };
  }
  return { tier: 0, kickers: ranks, name: 'High Card' };
}

function compareHands(a: HandRank, b: HandRank): number {
  if (a.tier !== b.tier) return a.tier - b.tier;
  for (let i = 0; i < Math.min(a.kickers.length, b.kickers.length); i++) {
    if (a.kickers[i] !== b.kickers[i]) return a.kickers[i] - b.kickers[i];
  }
  return 0;
}

/**
 * Evaluate the best poker hand from hole cards + community cards.
 * Returns the hand name (e.g. "Two Pair", "Flush").
 * Works with 2-7 total cards.
 */
export function evaluateHand(holeCards: string[], communityCards: string[]): string {
  const all = [...holeCards, ...communityCards];
  if (all.length < 2) return '';

  if (all.length <= 5) {
    if (all.length < 5) {
      return evaluate5while(all);
    }
    return evaluate5(all).name;
  }

  let best: HandRank | null = null;
  for (const combo of combinations(all, 5)) {
    const result = evaluate5(combo);
    if (!best || compareHands(result, best) > 0) {
      best = result;
    }
  }
  return best?.name ?? '';
}

function evaluate5while(cards: string[]): string {
  const parsed = cards.map(cardValue).sort((a, b) => b.r - a.r);
  const ranks = parsed.map((c) => c.r);

  const counts: Record<number, number> = {};
  for (const r of ranks) counts[r] = (counts[r] || 0) + 1;
  const groups = Object.entries(counts)
    .map(([r, c]) => ({ rank: Number(r), count: c }))
    .sort((a, b) => b.count - a.count || b.rank - a.rank);

  if (cards.length === 2) {
    return groups[0].count === 2 ? 'Pair' : 'High Card';
  }
  if (groups[0].count === 4) return 'Four of a Kind';
  if (groups[0].count === 3 && groups.length >= 2 && groups[1].count === 2) return 'Full House';
  if (groups[0].count === 3) return 'Three of a Kind';
  if (groups[0].count === 2 && groups.length >= 2 && groups[1].count === 2) return 'Two Pair';
  if (groups[0].count === 2) return 'Pair';
  return 'High Card';
}
